import clsx from "clsx";
import { useState } from "react";
import { Button } from "@/components/ui/button/Button";
import { Alert } from "@/components/ui/alert";
import {
  CC_ALERT_INFO_BODY,
  CC_ALERT_INFO_BORDER,
  CC_ALERT_INFO_PANEL,
  CC_ALERT_INFO_TITLE,
  CC_PANEL,
} from "@/components/case-checking/caseCheckingChrome";
import {
  clearedRerunSourceLabel,
  formatClearedProgressPhrase,
} from "@/components/case-checking/guidedReviewProgress";
import { reviewWorkflowStepLabel } from "@/components/case-checking/CaseReviewWorkflow";
import type { CaseReviewOnRerunMetadata, ClearedPriorReview } from "@/services/api/case-checking";

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function ReviewPostRerunNotice({
  cleared,
  reviewOnRerun,
  isReCheckInProgress,
  carryRetryBusy,
  acknowledging,
  onOpenPreviousReviews,
  onAcknowledge,
  onRetryCarry,
}: {
  cleared: ClearedPriorReview | null | undefined;
  reviewOnRerun: CaseReviewOnRerunMetadata | null;
  isReCheckInProgress: boolean;
  carryRetryBusy?: boolean;
  acknowledging?: boolean;
  onOpenPreviousReviews: () => void;
  onAcknowledge: () => void;
  onRetryCarry?: () => void;
}) {
  const [carryAppliedDismissed, setCarryAppliedDismissed] = useState(false);
  const carryFailed = reviewOnRerun?.mode === "carry" && reviewOnRerun.apply_status === "failed" && !isReCheckInProgress;
  const carryApplied =
    !carryAppliedDismissed &&
    reviewOnRerun?.mode === "carry" &&
    reviewOnRerun.apply_status === "applied" &&
    !isReCheckInProgress;
  const showCleared = cleared && !cleared.acknowledged_at && reviewOnRerun?.mode !== "carry";

  if (isReCheckInProgress && reviewOnRerun?.mode === "fresh") {
    return (
      <Alert variant="info" data-testid="review-rerun-in-progress">
        <p className="text-sm">Case re-check in progress. You will start a fresh guided review when it finishes.</p>
      </Alert>
    );
  }

  if (carryFailed) {
    return (
      <Alert variant="warning" data-testid="review-carry-apply-failed">
        <div className="space-y-3">
          <p className="font-medium">Case re-checked, but Ani could not fully carry your saved review.</p>
          {reviewOnRerun?.apply_error ? <p className="text-sm opacity-90">{reviewOnRerun.apply_error}</p> : null}
          <div className="flex flex-wrap gap-2">
            {onRetryCarry ? (
              <Button color="indigo" type="button" loading={carryRetryBusy} onClick={onRetryCarry} data-testid="review-carry-retry">
                Retry restore
              </Button>
            ) : null}
            <Button outline type="button" onClick={onOpenPreviousReviews}>Previous reviews</Button>
          </div>
        </div>
      </Alert>
    );
  }

  if (carryApplied) {
    return (
      <div
        className={clsx(
          CC_PANEL,
          CC_ALERT_INFO_PANEL,
          CC_ALERT_INFO_BORDER,
          "flex flex-nowrap items-center gap-3 py-2.5 pl-3 pr-2.5",
        )}
        role="status"
        data-testid="review-carry-applied"
      >
        <p className={clsx("min-w-0 flex-1 text-sm whitespace-nowrap", CC_ALERT_INFO_BODY)}>
          <span className={CC_ALERT_INFO_TITLE}>Case re-checked:</span> your review was carried into the updated results.
          <span className="text-sky-700/80 dark:text-sky-200/75"> · Recheck overrides that no longer match.</span>
        </p>
        <Button
          color="indigo"
          type="button"
          className="shrink-0"
          onClick={() => setCarryAppliedDismissed(true)}
          data-testid="review-carry-applied-got-it"
        >
          Got it
        </Button>
      </div>
    );
  }

  if (!showCleared || !cleared) return null;

  const reasonLabel = clearedRerunSourceLabel(cleared.reason?.source);
  const clearedItems = formatClearedProgressPhrase(cleared);
  const stepLabel = reviewWorkflowStepLabel(cleared.prior_current_step);

  return (
    <Alert variant="warning" data-testid="review-cleared-notice">
      <div className="space-y-3">
        <p className="font-medium">
          Case re-checked on {formatWhen(cleared.cleared_at)} after {reasonLabel}. You are starting a fresh guided review.
        </p>
        <p className="text-sm opacity-90">
          Your earlier work ({stepLabel}
          {clearedItems ? ` · ${clearedItems}` : ""}) is in Previous reviews.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button outline type="button" onClick={onOpenPreviousReviews} data-testid="review-cleared-notice-previous-reviews">
            Previous reviews
          </Button>
          <Button color="indigo" type="button" loading={acknowledging} onClick={onAcknowledge} data-testid="review-cleared-notice-acknowledge">
            Got it
          </Button>
        </div>
      </div>
    </Alert>
  );
}
